import Editor, { type OnMount } from "@monaco-editor/react";
import { useRef } from "react";

// monaco-editor no está como dep directa; inferimos el tipo desde OnMount
type MonacoEditorInstance = Parameters<OnMount>[0];
type MonacoInstance = Parameters<OnMount>[1];

interface Props {
  value: string;
  onChange?: (v: string) => void;
  language?: "typescript" | "javascript" | "sql";
  height?: number | string;
  readOnly?: boolean;
  minimap?: boolean;
  className?: string;
  /** Llamado cuando Monaco está montado. Útil para el test runtime que necesita acceso al worker. */
  onMount?: (editor: MonacoEditorInstance, monaco: MonacoInstance) => void;
  /** Llamado cuando cambian los markers (errores/warnings TS). */
  onMarkersChange?: (markers: { errors: number; warnings: number }) => void;
}

const VSCODE_DARK = "vscode-dark-custom";

export function CodeEditor({
  value,
  onChange,
  language = "typescript",
  height = 240,
  readOnly = false,
  minimap = false,
  className,
  onMount,
  onMarkersChange,
}: Props) {
  const editorRef = useRef<MonacoEditorInstance | null>(null);

  const handleMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;
    // Tema custom (solo se define una vez)
    monaco.editor.defineTheme(VSCODE_DARK, {
      base: "vs-dark",
      inherit: true,
      rules: [
        { token: "comment", foreground: "6A9955", fontStyle: "italic" },
        { token: "keyword", foreground: "569CD6" },
        { token: "string", foreground: "CE9178" },
        { token: "number", foreground: "B5CEA8" },
        { token: "type", foreground: "4EC9B0" },
        { token: "class", foreground: "4EC9B0" },
        { token: "function", foreground: "DCDCAA" },
        { token: "variable", foreground: "9CDCFE" },
        { token: "identifier", foreground: "9CDCFE" },
      ],
      colors: {
        "editor.background": "#0e0e13",
        "editor.foreground": "#d4d4d4",
        "editorLineNumber.foreground": "#4b4b56",
        "editorLineNumber.activeForeground": "#a1a1aa",
        "editor.selectionBackground": "#3b1c5e",
        "editor.lineHighlightBackground": "#1c1c24",
      },
    });
    monaco.editor.setTheme(VSCODE_DARK);

    // TypeScript permisivo: no chequear modules ni semantic en mini editors
    monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
      target: monaco.languages.typescript.ScriptTarget.ES2020,
      module: monaco.languages.typescript.ModuleKind.ESNext,
      allowNonTsExtensions: true,
      strict: false,
      noImplicitAny: false,
      moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
      lib: ["es2020", "dom"],
    });

    if (onMarkersChange) {
      monaco.editor.onDidChangeMarkers(() => {
        const model = editor.getModel();
        if (!model) return;
        const markers = monaco.editor.getModelMarkers({ resource: model.uri });
        const errors = markers.filter((m: { severity: number }) => m.severity === 8).length;
        const warnings = markers.filter((m: { severity: number }) => m.severity === 4).length;
        onMarkersChange({ errors, warnings });
      });
    }

    onMount?.(editor, monaco);
  };

  return (
    <div className={`overflow-hidden rounded-md border border-border ${className ?? ""}`}>
      <Editor
        height={height}
        language={language}
        value={value}
        onChange={(v) => onChange?.(v ?? "")}
        onMount={handleMount}
        theme={VSCODE_DARK}
        options={{
          readOnly,
          minimap: { enabled: minimap },
          fontSize: 13,
          fontFamily: "JetBrains Mono, ui-monospace, monospace",
          lineHeight: 20,
          tabSize: 2,
          scrollBeyondLastLine: false,
          automaticLayout: true,
          renderLineHighlight: "all",
          smoothScrolling: true,
          bracketPairColorization: { enabled: true },
          padding: { top: 10, bottom: 10 },
          wordWrap: "on",
          // Cuando el editor no tiene overflow vertical (caso readonly con
          // height calculada), no captures el wheel — dejá que el scroll
          // suba al contenedor padre.
          scrollbar: {
            alwaysConsumeMouseWheel: false,
          },
        }}
      />
    </div>
  );
}
